import { zodResolver } from '@hookform/resolvers/zod';
import type { PlatformChargeResource } from '@majuscule/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ellipsis, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useToast } from '@/components/toast';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CardBar, CardFoot } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, Fields2, InputGroup } from '@/components/ui/field';
import { MonthInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { formatMonthLabel } from '@/lib/date-input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Cell2,
} from '@/components/ui/table';
import { internalFetch, internalSend } from '@/lib/api';
import { currentMonth, formatEurCents, sumEuros } from '@/lib/format';

const BASE = '/internal/platform-charges';
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const EURO_RE = /^\d+(\.\d{1,6})?$/;

/** Shared with the overview page, which shows the charge count in its tab and stat card. */
export const platformChargesQuery = queryOptions({
  queryKey: ['platform-charges'],
  queryFn: () => internalFetch<{ charges: PlatformChargeResource[] }>(BASE),
});

const formSchema = z.object({
  label: z.string().min(1, 'Libellé obligatoire').max(200),
  frequency: z.enum(['monthly', 'annual']),
  cost: z.string().regex(EURO_RE, 'Montant en euros'),
  startMonth: z.string().regex(MONTH_RE, 'Mois de début requis'),
  endMonth: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: 'Mensuel',
  annual: 'Annuel (lissé)',
};

function emptyValues(): FormValues {
  return { label: '', frequency: 'monthly', cost: '', startMonth: currentMonth(), endMonth: '' };
}

interface PlatformChargesSectionProps {
  /** Onglets de la carte, placés à gauche de la barre. */
  tabs: ReactNode;
}

/**
 * Onglet « Frais généraux » de la vue d'ensemble : barre de la carte (onglets +
 * ajout), tableau des charges fixes, pied, dialogue de saisie et confirmation de
 * suppression.
 */
export function PlatformChargesSection({ tabs }: PlatformChargesSectionProps): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformChargeResource | null>(null);
  const [deleting, setDeleting] = useState<PlatformChargeResource | null>(null);

  const charges = useQuery(platformChargesQuery);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: emptyValues(),
  });
  const errors = form.formState.errors;

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['platform-charges'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  const toPayload = (values: FormValues): Record<string, unknown> => ({
    label: values.label,
    frequency: values.frequency,
    cost: values.cost,
    startMonth: values.startMonth,
    endMonth:
      values.endMonth !== undefined && MONTH_RE.test(values.endMonth) ? values.endMonth : null,
  });

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      editing === null
        ? internalSend<PlatformChargeResource>('POST', BASE, toPayload(values))
        : internalSend<PlatformChargeResource>('PATCH', `${BASE}/${editing.id}`, toPayload(values)),
    onSuccess: () => {
      closeModal();
      refresh();
      notify(editing === null ? 'Charge fixe ajoutée' : 'Charge fixe mise à jour');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => internalSend<undefined>('DELETE', `${BASE}/${id}`),
    onSuccess: () => {
      setDeleting(null);
      refresh();
      notify('Charge fixe supprimée');
    },
  });

  const openCreate = (): void => {
    setEditing(null);
    form.reset(emptyValues());
    setOpen(true);
  };

  const openEdit = (charge: PlatformChargeResource): void => {
    setEditing(charge);
    form.reset({
      label: charge.label,
      frequency: charge.frequency,
      cost: charge.cost,
      startMonth: charge.startMonth,
      endMonth: charge.endMonth ?? '',
    });
    setOpen(true);
  };

  function closeModal(): void {
    setOpen(false);
    setEditing(null);
    save.reset();
    form.reset();
  }

  const rows = charges.data?.charges ?? [];
  const activeRows = rows.filter((c) => c.active);
  const totalMonthly = sumEuros(activeRows.map((c) => c.monthlyCost));

  return (
    <>
      <CardBar
        start={tabs}
        end={
          <Button type="button" size="sm" onClick={openCreate}>
            <Plus aria-hidden="true" />
            Ajouter une charge
          </Button>
        }
      />
      <Table wrapperClassName="min-w-0" className="min-w-[640px]">
        <TableHeader>
          <TableRow>
            <TableHead>Libellé</TableHead>
            <TableHead>Fréquence</TableHead>
            <TableHead kind="num">Coût par mois (HT)</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead kind="act">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((charge) => (
            <TableRow key={charge.id}>
              <TableCell kind="name">
                <Cell2
                  primary={charge.label}
                  secondary={`${formatEurCents(charge.cost)} ${
                    charge.frequency === 'annual' ? 'par an' : 'par mois'
                  }${charge.endMonth !== null ? `, jusqu'à ${formatMonthLabel(charge.endMonth)}` : ''}`}
                />
              </TableCell>
              <TableCell>{FREQUENCY_LABELS[charge.frequency] ?? charge.frequency}</TableCell>
              <TableCell kind="num">{formatEurCents(charge.monthlyCost)}</TableCell>
              <TableCell>
                <Badge variant={charge.active ? 'success' : 'neutral'}>
                  {charge.active ? 'Actif' : 'Inactif'}
                </Badge>
              </TableCell>
              <TableCell kind="act">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Actions pour ${charge.label}`}
                    >
                      <Ellipsis aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() => {
                        openEdit(charge);
                      }}
                    >
                      <Pencil aria-hidden="true" />
                      Modifier
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => {
                        setDeleting(charge);
                      }}
                    >
                      <Trash2 aria-hidden="true" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
          {charges.isLoading ? <TableEmptyRow colSpan={5}>Chargement…</TableEmptyRow> : null}
          {rows.length === 0 && !charges.isLoading ? (
            <TableEmptyRow colSpan={5}>
              Aucune charge fixe. Ajoutez vos frais généraux (loyer, abonnements…).
            </TableEmptyRow>
          ) : null}
        </TableBody>
        {rows.length > 0 ? (
          <TableFooter>
            <TableRow>
              <TableCell>Total des frais généraux</TableCell>
              <TableCell />
              <TableCell kind="num">{formatEurCents(totalMonthly)}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
      <CardFoot
        start={`${activeRows.length} ${activeRows.length > 1 ? 'charges fixes actives' : 'charge fixe active'}`}
        end="Charges fixes de la structure (loyer, abonnements outils…), déduites de la marge globale sans rattachement à un client"
      />

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            closeModal();
          }
        }}
      >
        <DialogContent>
          <DialogHead
            title={editing === null ? 'Nouvelle charge fixe' : 'Modifier la charge fixe'}
            description="Frais général de la structure, décompté de la marge globale."
          />
          <form
            id="platform-charge-form"
            onSubmit={(event) => {
              void form.handleSubmit((values) => {
                save.mutate(values);
              })(event);
            }}
            className="contents"
          >
            <DialogBody>
              <div className="grid gap-4">
                <Field label="Libellé" required error={errors.label?.message}>
                  <Input placeholder="Loyer du bureau" {...form.register('label')} />
                </Field>
                <Fields2>
                  <Field label="Fréquence" required error={errors.frequency?.message}>
                    <NativeSelect {...form.register('frequency')}>
                      <option value="monthly">Mensuel</option>
                      <option value="annual">Annuel (lissé sur 12 mois)</option>
                    </NativeSelect>
                  </Field>
                  <Field label="Coût" required error={errors.cost?.message}>
                    <InputGroup suffix="€">
                      <Input
                        numeric
                        inputMode="decimal"
                        placeholder="1200"
                        {...form.register('cost')}
                      />
                    </InputGroup>
                  </Field>
                </Fields2>
                <Fields2>
                  <Field label="Mois de début" required error={errors.startMonth?.message}>
                    <Controller
                      control={form.control}
                      name="startMonth"
                      render={({ field }) => (
                        <MonthInput
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </Field>
                  <Field label="Mois de fin" hint="Optionnel" error={errors.endMonth?.message}>
                    <Controller
                      control={form.control}
                      name="endMonth"
                      render={({ field }) => (
                        <MonthInput
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </Field>
                </Fields2>
                {save.isError ? (
                  <Callout variant="destructive">Échec de l&apos;enregistrement.</Callout>
                ) : null}
              </div>
            </DialogBody>
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button type="submit" form="platform-charge-form" size="lg" disabled={save.isPending}>
              {editing === null ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate(deleting.id);
          }
        }}
        title={deleting !== null ? `Supprimer « ${deleting.label} » ?` : 'Supprimer ?'}
        subtitle="Action irréversible."
        confirmLabel="Supprimer"
        busy={remove.isPending}
        variant="destructive"
        note="La charge disparaît du calcul de marge des mois suivants. Pour l'arrêter en gardant l'historique, renseignez plutôt un mois de fin."
      />
    </>
  );
}
